/* eslint-disable object-shorthand */
import { useEffect, useRef, useCallback } from 'react';

import { useEditorAnnotations } from '@confluence/inline-comments-hooks/entry-points/useEditorAnnotations.ts';
import { useRendererActions } from '@confluence/renderer-actions/entry-points/RendererActionsContext.ts';

import { useUnreadComments } from './useUnreadComments.ts';
import { useViewedComments } from './useViewedComments.ts';
import { isValidUnreadComment, commentIsDangling } from '../utils/unreadCommentsUtils.ts';

export type AnnotationStatus = {
	threadKey: string;
};

export interface UseIsDanglingCallbackProps {
	pageMode: 'view' | 'edit' | undefined;
}

export interface UseIsDanglingCallbackResult {
	isDanglingCallback: (markerRef: string | null | undefined) => boolean;
}

export const useIsDanglingCallback = ({
	pageMode,
}: UseIsDanglingCallbackProps): UseIsDanglingCallbackResult => {
	const [editorAnnotationsState] = useEditorAnnotations();
	const { annotations } = editorAnnotationsState;

	const rendererActionsResult = useRendererActions();
	const { rendererActions } = rendererActionsResult || {};

	const isDanglingCallback = useCallback(
		(markerRef: string | null | undefined) => {
			if (!pageMode) {
				return false;
			}

			return commentIsDangling({
				pageMode,
				annotationsInEditorDoc: new Set(annotations),
				rendererAnnotations: new Set(
					rendererActions
						?.getAnnotationMarks()
						?.map((ann) => ann.attrs.id)
						.filter(Boolean) || [],
				),
			})(markerRef);
		},
		[annotations, rendererActions, pageMode],
	);

	return {
		isDanglingCallback,
	};
};

// Helper function to process added annotations (reopened / undangled)
const processAddedAnnotations = ({
	currentList,
	previousThreadKeys,
	commentsDataMap,
	viewedComments,
	userId,
	isDanglingCallback,
}: {
	currentList: AnnotationStatus[];
	previousThreadKeys: Set<string>;
	commentsDataMap: { inline: Record<string, any> };
	viewedComments: Set<string>;
	userId: string;
	isDanglingCallback: (markerRef: string | null | undefined) => boolean;
}): Set<string> => {
	const addedCommentIds = new Set<string>();

	for (const annotation of currentList) {
		if (!previousThreadKeys.has(annotation.threadKey)) {
			const commentData = commentsDataMap.inline[annotation.threadKey];

			if (commentData) {
				// Early return if comment is already viewed
				if (viewedComments.has(commentData.id)) {
					return addedCommentIds;
				}

				// Add the main comment ID if it's a valid unread comment
				if (
					isValidUnreadComment({
						comment: commentData,
						currentUserId: userId,
						viewedComments,
						isDanglingCallback: isDanglingCallback,
					})
				) {
					addedCommentIds.add(commentData.id);
				}

				// Add all reply IDs if they're valid unread comments
				commentData.replies?.forEach((reply: any) => {
					if (reply) {
						// Skip if reply is already viewed
						if (viewedComments.has(reply.id)) {
							return;
						}

						if (
							isValidUnreadComment({
								comment: reply,
								currentUserId: userId,
								viewedComments,
								isDanglingCallback: isDanglingCallback,
							})
						) {
							addedCommentIds.add(reply.id);
						}
					}
				});
			}
		}
	}

	return addedCommentIds;
};

// Helper function to process removed annotations (resolved / dangled)
const processRemovedAnnotations = ({
	previousList,
	currentThreadKeys,
	commentsDataMap,
}: {
	previousList: AnnotationStatus[];
	currentThreadKeys: Set<string>;
	commentsDataMap: { inline: Record<string, any> };
}): Set<string> => {
	const removedCommentIds = new Set<string>();

	for (const annotation of previousList) {
		if (!currentThreadKeys.has(annotation.threadKey)) {
			const commentData = commentsDataMap.inline[annotation.threadKey];

			if (commentData) {
				// Add the main comment ID
				removedCommentIds.add(commentData.id);

				// Add all reply IDs
				commentData.replies?.forEach((reply: any) => {
					if (reply) {
						removedCommentIds.add(reply.id);
					}
				});
			}
		}
	}

	return removedCommentIds;
};

// useDanglingAndResolvedHandler captures dangling and inline resolves scenarios by detecting changes in orderedActiveAnnotationIdList
// this hook is only used for pages, not whiteboards and databases
export const useDanglingAndResolvedHandler = ({
	orderedActiveAnnotationIdList,
	commentsDataMap,
	userId,
	pageId,
	isDanglingCallback,
}: {
	orderedActiveAnnotationIdList: AnnotationStatus[];
	commentsDataMap: { inline: Record<string, any>; general: Record<string, any> };
	userId: string;
	pageId: string;
	isDanglingCallback: ((markerRef: string | null | undefined) => boolean) | undefined;
}) => {
	const [{ isUpdatingUnreadComments }, { updateUnreadCommentIDSet }] = useUnreadComments();

	const { getViewedCommentsCacheOnly } = useViewedComments({ pageId });

	// Keep track of the previous orderedActiveAnnotationIdList
	const previousOrderedActiveAnnotationIdListRef = useRef<AnnotationStatus[]>(
		orderedActiveAnnotationIdList,
	);

	// Handle the async getViewedComments call
	const processAnnotations = useCallback(async () => {
		const previousList = previousOrderedActiveAnnotationIdListRef.current;
		const currentList = orderedActiveAnnotationIdList;

		// Early exit if lists are identical (same length)
		if (previousList.length === currentList.length) {
			return;
		}

		// Build thread key sets for processing
		const previousThreadKeys = new Set(previousList.map((annotation) => annotation.threadKey));
		const currentThreadKeys = new Set(currentList.map((annotation) => annotation.threadKey));

		const viewedCommentsResult = getViewedCommentsCacheOnly();

		if (!viewedCommentsResult.success || !viewedCommentsResult.data) {
			return;
		}

		const viewedComments = viewedCommentsResult.data;

		// Process added annotations (reopened / undangled)
		const addedCommentIds = processAddedAnnotations({
			currentList,
			previousThreadKeys,
			commentsDataMap,
			viewedComments,
			userId,
			isDanglingCallback: isDanglingCallback!,
		});

		// Process removed annotations (resolved / dangled)
		const removedCommentIds = processRemovedAnnotations({
			previousList,
			currentThreadKeys,
			commentsDataMap,
		});

		// Update unread comment ID set for added annotations
		if (addedCommentIds.size > 0) {
			updateUnreadCommentIDSet(addedCommentIds, 'add');
		}

		// Update unread comment ID set for removed annotations
		if (removedCommentIds.size > 0) {
			updateUnreadCommentIDSet(removedCommentIds, 'remove');
		}

		// Update the previous list ref to current list for next comparison
		previousOrderedActiveAnnotationIdListRef.current = currentList;
	}, [
		orderedActiveAnnotationIdList,
		commentsDataMap,
		userId,
		isDanglingCallback,
		getViewedCommentsCacheOnly,
		updateUnreadCommentIDSet,
	]);

	useEffect(() => {
		// skip entirely if:
		// - feature flag is disabled
		// - isDanglingCallback check is not enabled
		// - isUpdatingUnreadComments being true means the unread query and viewed comments query are not complete yet, which means we shouldn't process the annotations yet
		if (!isDanglingCallback || isUpdatingUnreadComments) {
			return;
		}

		void processAnnotations();

		// eslint-disable-next-line react-hooks/exhaustive-deps -- exclude commentsDataMap because we don't need to rerun this hook when it changes
	}, [processAnnotations, isUpdatingUnreadComments]);
};
